import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { _chunk, _isBlank, _isEmpty, _isNil, _isNotBlank, _isNotEmpty, _notNil, _set } from '@/littledash';

import type { AltId, Animal } from '@/model/Animal.model';
import type { ID } from '@/model/Common.model';
import type { StudyApiId } from '@/model/Study.model';
import InVivoError from '@/model/InVivoError';
import type { CellChange, ChangeSource } from '@/support/hot.types';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';

import { featuresSelector } from '@/support/Selectors';
import { ApiService } from '@/support/ApiService';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Spinner from '@/components/UI/Spinner';
import Switch from '@/components/UI/FormElements/Switch';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { ModalActions, ModalContainer, ModalHeader, useModalAction } from '@/utils/modal';

import {
  buildHoTcolumns,
  verifyUniqueBatch,
  type AnimalIdentifierData,
  type VerifyUniqueBatchItem,
  type VerifyUniqueBatchResult,
} from './AssignIdentifiers.utils';
import AssignIdentifiersTable from './AssignIdentifiersTable';
import BulkGenerateForm from './BulkGenerateForm';
import './AssignIdentifiers.scss';

const CHUNK_SIZE = 50;

const TAG_PROP = 'alt_ids.tag';

/**
 * `afterChange` sources that trigger an auto-save: typed edits plus clipboard
 * paste/cut (paste is reported differently across Handsontable versions).
 */
const AUTO_SAVE_SOURCES = new Set<string>(['edit', 'paste', 'CopyPaste.paste', 'CopyPaste.cut']);

/**
 * Reason recorded for every auto-saved identifier change when the
 * `reason_for_change` feature flag is active. Auto-save fires per edit without a
 * modal prompt, so the reason is hardcoded; manual Save (auto-save off) instead
 * opens the `BULK_REASON_FOR_CHANGE` modal to collect it from the user.
 */
export const ASSIGN_IDENTIFIERS_REASON_FOR_CHANGE = 'Assign Identifiers bulk update';

const assignIdentifiersSelector = createSelector([featuresSelector], (features) => ({
  assignIdentifiersTagUnique: features?.assign_identifiers_tag_unique ?? false,
  reasonForChangeEnabled: features?.reason_for_change === true,
}));

const altIdFields: Array<AltId> = ['tail', 'ear', 'tag', 'donor'];

/**
 * Determines whether a set of pending changes modifies an existing value (vs only adding new ones).
 * Adding an identifier to a previously-empty field is exempt from reason-for-change; changing or
 * clearing a field that already had a value requires a reason.
 */
const changesExistingValue = (
  pendingChanges: Record<ID, AnimalIdentifierData>,
  originalById: Map<ID, AnimalIdentifierData>
): boolean =>
  Object.values(pendingChanges).some((change) => {
    const original = originalById.get(change.id);
    if (_isNil(original)) {
      return false;
    }
    if (_isNotBlank(original.name) && original.name !== change.name) {
      return true;
    }
    return altIdFields.some((field) => {
      const before = original.alt_ids?.[field];
      const after = change.alt_ids?.[field];
      return _isNotBlank(before) && before !== after;
    });
  });

interface AssignIdentifiersProps {
  animals: Array<Animal>;
  studyApiId?: StudyApiId;
  handleCallback: (message?: string) => void;
  closeModal: () => void;
}

const initialAnimalData = (animals: Array<Animal>): Array<AnimalIdentifierData> =>
  animals.map(({ id, api_id, name, cage, alt_ids: altIds }) => ({
    id,
    api_id,
    name: name ?? '',
    cage,
    alt_ids: { ...altIds },
  }));

const AssignIdentifiers: FC<AssignIdentifiersProps> = ({ animals, studyApiId, handleCallback, closeModal }) => {
  const { assignIdentifiersTagUnique, reasonForChangeEnabled } = useSelector(assignIdentifiersSelector);
  const { openModal } = useModalAction();
  const originalById = useMemo(
    () => new Map<ID, AnimalIdentifierData>(initialAnimalData(animals).map((a) => [a.id, a])),
    [animals]
  );
  const [data, setData] = useState<Array<AnimalIdentifierData>>(initialAnimalData(animals));
  const [changes, setChanges] = useState<Record<ID, AnimalIdentifierData>>({});
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [reloadOnClose, setReloadOnClose] = useState(false);

  const [updatingAnimals, setUpdatingAnimals] = useState(false);
  const [requestError, setRequestError] = useState<Error | false>(false);
  const [cellEditing, setCellEditing] = useState(false);

  const updateAnimals = async (animals: Array<AnimalIdentifierData>, reasonForChange?: string) =>
    ApiService.call({
      endpoint: 'PATCH /api/v1/animals/identifiers',
      body: {
        animals: animals.map(({ api_id, name, alt_ids }) => ({
          api_id: api_id!,
          name,
          alt_ids: _isNotEmpty(alt_ids) ? alt_ids : null,
        })),
        ...(_notNil(reasonForChange) ? { reason_for_change: reasonForChange } : {}),
      },
      options: { onError: { throw: true, toast: false, slug: 'assign-identifiers-bulk-update' } },
    });

  const switchRef = useRef(false);
  const tableRef = useRef<HotTableRef>(null);

  // Auto-save is held while the single server-side uniqueness batch check is in
  // flight, so a save only fires once the edited cells' validity is known.
  const batchInFlight = useRef(false);
  const heldAutoSaveChanges = useRef<Map<string, CellChange>>(new Map());
  const autoSaveRef = useRef<(changes: Array<CellChange>) => void>(() => {});
  // Rows whose current tag value the batch endpoint flagged as an `existing`
  // (server-wide) conflict, keyed `${row}:${value}` so a subsequent edit clears
  // the flag. Read synchronously by the Tag validator via `getServerConflict`.
  const tagServerConflicts = useRef<Set<string>>(new Set());
  // Mirror of `changes` for async callbacks that would otherwise close over
  // stale state.
  const changesRef = useRef(changes);

  const getHotInstance = () => tableRef.current?.hotInstance ?? null;

  const getHotInstanceRef = useRef(getHotInstance);

  const getServerConflict = useCallback(
    (row: number, value: string): boolean => tagServerConflicts.current.has(`${row}:${value}`),
    []
  );

  // Release every held change whose cell resolved valid. Deliberately not gated
  // on the table-wide invalid flag, so a valid edit still saves when an
  // unrelated cell is invalid.
  const flushHeldAutoSave = useCallback(() => {
    if (!switchRef.current) {
      heldAutoSaveChanges.current.clear();
      return;
    }
    const held = Array.from(heldAutoSaveChanges.current.values());
    heldAutoSaveChanges.current.clear();
    if (held.length === 0) {
      return;
    }
    const hot = getHotInstanceRef.current();
    const sourceData = (hot?.getSourceData() ?? []) as Array<AnimalIdentifierData>;
    const validHeld = held.filter(([row, column, , newValue]) => {
      const col = hot?.propToCol(column as string);
      // `propToCol` yields a non-numeric value or -1 for an unknown prop; treat
      // that like a missing column and keep the change rather than reading an
      // invalid cell.
      if (typeof col !== 'number' || col < 0) {
        return true;
      }
      // Skip cells the validator marked invalid. Uses `!== false` because a
      // cell that resolved valid asynchronously may still report `undefined`.
      if (hot?.getCellMeta(row, col)?.valid === false) {
        return false;
      }
      // A duplicate tag is known synchronously, so never persist a value that
      // another row currently holds. Only when uniqueness is enforced and the
      // value is non-blank — clearing a tag is always valid.
      const tagValue = _notNil(newValue) ? String(newValue) : '';
      if (assignIdentifiersTagUnique && column === TAG_PROP && _isNotBlank(tagValue)) {
        const duplicateInTable = sourceData.some(
          (dataRow, index) => index !== row && _notNil(dataRow?.alt_ids) && dataRow.alt_ids.tag === tagValue
        );
        if (duplicateInTable) {
          return false;
        }
      }
      return true;
    });
    if (validHeld.length > 0) {
      autoSaveRef.current(validHeld);
    }
  }, [assignIdentifiersTagUnique]);

  /**
   * Applies a batch uniqueness response to the server-conflict lookup: rows
   * reported non-unique are flagged, rows reported unique are cleared. Keyed by
   * the value we sent so a later edit to a different value clears the flag.
   */
  const applyBatchResults = useCallback(
    (sentItems: Array<VerifyUniqueBatchItem>, results: Array<VerifyUniqueBatchResult>) => {
      const sentByRef = new Map(sentItems.map((item) => [item.ref, item.value]));
      results.forEach((result) => {
        const value = sentByRef.get(result.ref) ?? result.value;
        const key = `${result.ref}:${value}`;
        if (result.unique) {
          tagServerConflicts.current.delete(key);
        } else {
          tagServerConflicts.current.add(key);
        }
      });
    },
    []
  );

  /**
   * Verifies the pass's tag edits with a single batch request, feeds the results
   * back into the validator via `tagServerConflicts`, re-validates the affected
   * rows so validity/`isInvalid` update, then releases any held auto-save.
   */
  const runBatchUniqueCheck = useCallback(
    (tagChanges: Array<CellChange>, shouldFlushAfter: boolean) => {
      const hot = getHotInstanceRef.current();
      const sourceData = (hot?.getSourceData() ?? []) as Array<AnimalIdentifierData>;
      const items = tagChanges.reduce<Array<VerifyUniqueBatchItem>>((acc, [row, , , newValue]) => {
        const value = _notNil(newValue) ? String(newValue) : '';
        if (_isBlank(value)) {
          return acc;
        }
        acc.push({ ref: String(row), value, animal_id: sourceData[row]?.api_id });
        return acc;
      }, []);

      if (_isEmpty(items)) {
        if (shouldFlushAfter && !batchInFlight.current) {
          flushHeldAutoSave();
        }
        return;
      }

      const itemRows = items.map((item) => Number(item.ref));
      batchInFlight.current = true;
      verifyUniqueBatch({ altIdType: 'tag', studyApiId, items })
        .then((results) => {
          applyBatchResults(items, results);
        })
        .catch((error) => {
          // Fail closed: an unverifiable value is treated as a conflict so it is
          // never persisted while the check is failing.
          items.forEach((item) => tagServerConflicts.current.add(`${item.ref}:${item.value}`));
          ExceptionHandler.captureException(
            new InVivoError('Assign Identifiers bulk tag uniqueness check failed', {
              cause: error,
              slug: 'assign-identifiers-tag-unique-batch',
            })
          );
        })
        .finally(() => {
          // Re-validate so the updated conflict set flows into cell validity and
          // the table-wide `isInvalid` flag. HoT runs validators asynchronously
          // (`setImmediate`), so release held auto-save only after that completes.
          const hot = getHotInstanceRef.current();
          if (_notNil(hot)) {
            hot.validateRows(itemRows, () => {
              batchInFlight.current = false;
              if (shouldFlushAfter) {
                flushHeldAutoSave();
              }
            });
            return;
          }
          batchInFlight.current = false;
          if (shouldFlushAfter) {
            flushHeldAutoSave();
          }
        });
    },
    [studyApiId, applyBatchResults, flushHeldAutoSave]
  );

  const columns = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs
      buildHoTcolumns({
        tagUnique: assignIdentifiersTagUnique,
        getServerConflict,
      }),
    [assignIdentifiersTagUnique, getServerConflict]
  );

  const handleChangePayload = (pendingChanges: Record<ID, AnimalIdentifierData>) => Object.values(pendingChanges);

  const handleSave = async (reasonForChange?: string) => {
    getHotInstance()?.updateSettings({ readOnly: true });
    setUpdatingAnimals(true);
    setRequestError(false);
    try {
      await Promise.all(
        _chunk(handleChangePayload(changes), CHUNK_SIZE).map((batch) => updateAnimals(batch, reasonForChange))
      );
      if (autoSaveEnabled) {
        getHotInstance()?.updateSettings({ readOnly: false });
      } else {
        handleCallback('Successfully assigned identifiers.');
      }
    } catch (error) {
      setRequestError(error as Error);
      getHotInstance()?.updateSettings({ readOnly: false });
    } finally {
      setUpdatingAnimals(false);
    }
  };

  const handleSubmit = async () => {
    if (isInvalid) {
      return Promise.reject(new Error('Table data invalid'));
    }
    if (reasonForChangeEnabled && changesExistingValue(changes, originalById)) {
      // Auto-save on: no per-edit modal prompt, hardcode a generic reason. Auto-save
      // off: collect the reason from the user via BULK_REASON_FOR_CHANGE (same
      // pattern as other bulk edit flows).
      if (autoSaveEnabled) {
        return handleSave(ASSIGN_IDENTIFIERS_REASON_FOR_CHANGE);
      }
      openModal('BULK_REASON_FOR_CHANGE', {
        title: 'Animal information has changed',
        onClick: (reasonForChange?: string) => handleSave(reasonForChange),
      });
      return Promise.resolve();
    }
    return handleSave();
  };

  const handleBeforeChange = (dataChanges: Array<CellChange> | null) => {
    if (_isNil(dataChanges)) {
      return;
    }
    const { updatedData, updatedChanges } = dataChanges.reduce<{
      updatedData: Array<AnimalIdentifierData>;
      updatedChanges: Record<ID, AnimalIdentifierData>;
    }>(
      ({ updatedData, updatedChanges }, [row, column, , newValue]) => {
        _set(updatedData, `[${row}].${column}`, newValue);
        const { id, api_id } = animals[row];
        const { name, alt_ids: altIds } = updatedChanges?.[id] ?? animals[row];
        const updatedAnimal: AnimalIdentifierData = {
          id,
          api_id,
          name: name ?? '',
          alt_ids: { ...altIds },
          cage: animals[row].cage,
        };
        _set(updatedAnimal, column as string, newValue);
        return {
          updatedData,
          updatedChanges: { ...updatedChanges, [id]: updatedAnimal },
        };
      },
      { updatedData: [...data], updatedChanges: { ...changes } }
    );
    setChanges(updatedChanges);
    setData(updatedData);
  };

  /**
   * Re-validates the edited rows plus any other row sharing an affected tag
   * value, fires a single batch uniqueness check for the pass's tag edits, and
   * releases a deferred auto-save once that check settles.
   */
  const handleAfterChange = (dataChanges: Array<CellChange> | null, source: ChangeSource | 'insert_row' | string) => {
    if (_isNil(dataChanges)) {
      return;
    }
    const rowsToValidate = dataChanges.map((change) => change[0]);
    const tagChanges = assignIdentifiersTagUnique ? dataChanges.filter((change) => change[1] === TAG_PROP) : [];

    if (_isNotEmpty(tagChanges)) {
      const affectedValues = new Set<string>();
      tagChanges.forEach(([, , oldValue, newValue]) => {
        if (_notNil(oldValue)) {
          affectedValues.add(String(oldValue));
        }
        if (_notNil(newValue)) {
          affectedValues.add(String(newValue));
        }
      });

      const sourceData = getHotInstance()?.getSourceData() ?? [];

      sourceData.forEach((row, index) => {
        const tag = row?.alt_ids?.tag;
        if (_notNil(tag) && affectedValues.has(String(tag)) && !rowsToValidate.includes(index)) {
          rowsToValidate.push(index);
        }
      });
    }

    getHotInstance()?.validateRows(rowsToValidate, () => {
      const autoSaving = AUTO_SAVE_SOURCES.has(source) && switchRef.current;
      const hot = getHotInstance();

      // Stage the just-edited (and dependent) changes for auto-save, unless the
      // edited cell already failed the synchronous validator.
      if (autoSaving) {
        const editedSyncInvalid = dataChanges.some(([row, column]) => {
          const col = hot?.propToCol(column as string);
          // `propToCol` yields a non-numeric value or -1 for an unknown prop.
          if (typeof col !== 'number' || col < 0) {
            return false;
          }
          return hot?.getCellMeta(row, col)?.valid === false;
        });

        if (!editedSyncInvalid) {
          for (const change of dataChanges) {
            heldAutoSaveChanges.current.set(`${change[0]}:${change[1]}`, change);
          }

          const tagCol = hot?.propToCol(TAG_PROP);
          if (_notNil(hot) && _isNil(tagCol) === false) {
            const sourceData = hot.getSourceData() as Array<AnimalIdentifierData>;
            const changedRowSet = new Set(dataChanges.map((change) => change[0]));
            rowsToValidate.forEach((row) => {
              if (changedRowSet.has(row)) {
                return;
              }
              const animal = sourceData[row];
              const id = animal?.id;
              const pendingAnimal = _notNil(id) ? changesRef.current[id] : undefined;
              const originalTag = animals[row]?.alt_ids?.tag;
              if (_notNil(pendingAnimal) && pendingAnimal?.alt_ids?.tag !== originalTag) {
                heldAutoSaveChanges.current.set(`${row}:${TAG_PROP}`, [
                  row,
                  TAG_PROP,
                  originalTag ?? null,
                  animal?.alt_ids?.tag ?? null,
                ]);
              }
            });
          }
        }
      }

      // A tag edit always triggers the server-side check (regardless of
      // auto-save) so `existing` conflicts mark the cell invalid and block a
      // manual Save too. The batch resolution flushes any held auto-save.
      if (assignIdentifiersTagUnique && _isNotEmpty(tagChanges)) {
        runBatchUniqueCheck(tagChanges, autoSaving);
      } else if (autoSaving && !batchInFlight.current) {
        flushHeldAutoSave();
      }
    });
  };

  const handleAutoSave = (dataChanges: Array<CellChange>) => {
    const updates = dataChanges.map(([row, column, , newValue]) => {
      const { id, api_id } = animals[row];
      const { name, alt_ids: altIds } = changes[id] ?? animals[row];
      const updatedAnimal: AnimalIdentifierData = {
        id,
        api_id,
        name: name ?? '',
        alt_ids: { ...altIds },
        cage: animals[row].cage,
      };
      _set(updatedAnimal, column as string, newValue);
      return updatedAnimal;
    });

    if (_isNotEmpty(updates)) {
      // Mirror the manual-save flow: flag the in-flight request so the footer
      // spinner shows and the modal can't be closed mid-save (handleExitModal).
      setUpdatingAnimals(true);
      updateAnimals(updates, reasonForChangeEnabled ? ASSIGN_IDENTIFIERS_REASON_FOR_CHANGE : undefined)
        .catch((error) => {
          setRequestError(error as Error);
        })
        .finally(() => {
          setUpdatingAnimals(false);
        });
    }
  };

  // Mirror the latest state-dependent callbacks into refs so the validator
  // columns don't need rebuilding on every render.
  useEffect(() => {
    getHotInstanceRef.current = getHotInstance;
    autoSaveRef.current = handleAutoSave;
    changesRef.current = changes;
  });

  // Track whether a cell editor is open so Save can be disabled mid-edit.
  // v16 has no `afterEndEditing` hook, so close is inferred by reading the
  // active editor's `isOpened()` after every event that can end editing
  // (commit, click-away deselect, Enter/Escape keydown), deferred a tick so the
  // editor's open/close state has settled before it is read.
  useEffect(() => {
    const hot = tableRef.current?.hotInstance;
    if (_isNil(hot)) {
      return;
    }
    const handleBeginEditing = () => setCellEditing(true);
    const syncEditingState = () => {
      setTimeout(() => {
        setCellEditing(tableRef.current?.hotInstance?.getActiveEditor()?.isOpened() ?? false);
      }, 0);
    };
    hot.addHook('afterBeginEditing', handleBeginEditing);
    hot.addHook('afterChange', syncEditingState);
    hot.addHook('afterDeselect', syncEditingState);
    hot.addHook('afterDocumentKeyDown', syncEditingState);
    return () => {
      hot.removeHook('afterBeginEditing', handleBeginEditing);
      hot.removeHook('afterChange', syncEditingState);
      hot.removeHook('afterDeselect', syncEditingState);
      hot.removeHook('afterDocumentKeyDown', syncEditingState);
    };
  }, []);

  const handleBulkAssign = (dataChanges: Array<CellChange>) => {
    setAutoSaveEnabled(false);
    switchRef.current = false;
    setReloadOnClose(false);
    const rowPropChanges = dataChanges.map(
      ([row, prop, value]) => [row, prop, value] as [number, string | number, unknown]
    );
    getHotInstance()?.setDataAtRowProp(rowPropChanges);
  };

  const handleExitModal = () => {
    if (!updatingAnimals) {
      if (autoSaveEnabled || reloadOnClose) {
        handleCallback();
      }
      closeModal();
    }
  };

  useEffect(() => {
    if (autoSaveEnabled && _isNotEmpty(Object.keys(changes))) {
      handleSubmit().catch(() => {
        setAutoSaveEnabled(false);
        switchRef.current = false;
        setReloadOnClose(true);
        toast.warning('Disabled autosave as pre existing changes have errors');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveEnabled]);

  const handleSwitch = () => {
    switchRef.current = !autoSaveEnabled;
    setAutoSaveEnabled(!autoSaveEnabled);
  };

  return (
    <ModalContainer size="wide" className="bg-white flex flex-column justify-between items-start">
      <ModalHeader
        title="Assign Identifiers to animals"
        closeModal={handleExitModal}
        closeDisabled={updatingAnimals}
        className="pa3 bb b--moon-gray w-100"
      />
      <div className="pa3 w-100">
        {requestError !== false && <ApiErrorBanner error={requestError as Error} className="mb3" />}
        <BulkGenerateForm data={animals} setData={handleBulkAssign} tagUnique={assignIdentifiersTagUnique} />
        <div className="ow-spreadsheet-styles assign-identifier-spreadsheet">
          <AssignIdentifiersTable
            data={data}
            columns={columns}
            innerRef={tableRef}
            beforeChange={handleBeforeChange}
            afterChange={handleAfterChange}
            maxRows={data.length}
            setInvalid={setIsInvalid}
          />
        </div>
      </div>
      <div className="pa3 bt b--moon-gray flex justify-between items-center w-100">
        <ModalActions
          onSubmit={handleSubmit}
          onCancel={handleExitModal}
          cancelBtnText="Close"
          submitButtonProps={{
            loading: updatingAnimals,
            disabled: updatingAnimals || autoSaveEnabled || isInvalid || cellEditing,
            tooltip: isInvalid
              ? spreadSheetValidationTooltip
              : cellEditing
                ? 'Finish editing the cell before saving'
                : undefined,
          }}
          cancelButtonProps={{ disabled: updatingAnimals }}
        />
        <div className="flex items-center">
          {autoSaveEnabled && updatingAnimals && (
            <div className="flex items-center mr3">
              <Spinner size="small" className="mr2" color="near-black" />
              <span className="f6 lh-title near-black">Saving</span>
            </div>
          )}
          <Switch value={autoSaveEnabled} onChange={handleSwitch} disabled={false} />
          <label className="ml2 mb0">Auto save</label>
        </div>
      </div>
    </ModalContainer>
  );
};

export default AssignIdentifiers;
